import { zodResolver } from "@hookform/resolvers/zod";
import { type ReactNode, useEffect, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  ComboBox,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
} from "@/components/ui/combobox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FieldError, Label } from "@/components/ui/field";
import { ItemDetail } from "@/components/ui/list-box";
import { Modal } from "@/components/ui/modal";
import { mutationResponse } from "@/utils/trpc";
import type { OrgDetail } from "../../types";

const schema = z.object({
  orgId: z.number(),
  testSpecId: z.number(),
});
type FormValues = z.infer<typeof schema>;

export function AddResultNotificationDialog({
  children,
  org,
}: {
  org: OrgDetail;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { data } = trpc.tests.all.useQuery();
  const addNotification =
    trpc.admin.org.addTestResultNotification.useMutation();

  const tests =
    data?.filter((ts) => {
      return !org.testResultNotifications.find(
        (trn) => trn.testSpecId === ts.id,
      );
    }) || [];

  const { control, handleSubmit, register, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    addNotification.mutate(
      values,
      mutationResponse({
        errorTitle: "Error adding notification",
        successMessage: "Notification added",
        onSuccess() {
          setOpen(false);
        },
      }),
    );
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      {children}
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Add test result notification</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              onSubmit={handleSubmit(onSubmit)}
              id="add-result-notification-form"
            >
              <input
                type="hidden"
                {...register("orgId", { valueAsNumber: true })}
                value={org.id}
              />
              <Controller
                control={control}
                name="testSpecId"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <ComboBox
                    {...field}
                    menuTrigger="focus"
                    selectedKey={field.value}
                    onSelectionChange={field.onChange}
                    isInvalid={!!error}
                    validationBehavior="aria"
                  >
                    <Label>Test spec</Label>
                    <ComboBoxInput />
                    <ComboBoxList items={tests}>
                      {(item) => (
                        <ComboBoxItem
                          id={item.id}
                          textValue={`${item.name} (${item.code})`}
                        >
                          <span className="min-w-0 flex-1 truncate">
                            {item.name}
                          </span>
                          <ItemDetail>{item.code}</ItemDetail>
                        </ComboBoxItem>
                      )}
                    </ComboBoxList>
                    <FieldError>{error?.message}</FieldError>
                  </ComboBox>
                )}
              />
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              type="submit"
              form="add-result-notification-form"
              isPending={addNotification.isPending}
            >
              Submit
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
